/**
 * SearchSummary.tsx — the results-page left rail.
 *
 * Renders the active hard filters as a FAITHFUL view of the query tree (TreeFilters) — every leaf keeps
 * its temporal scope / negation / grouping instead of being flattened into lossy chips — plus the soft
 * "Must-haves" (scored criteria) and a live count. Removing a chip edits the tree directly and marks the
 * search dirty → "Update search". "Edit all" opens the full FilterModal.
 */

import { useState } from 'react';
import { Sparkles, SlidersHorizontal, X, Plus, ArrowUp, Loader2, GripVertical } from 'lucide-react';
import type { Group } from '@/lib/talentPool.api';
import { TreeFilters } from './TreeFilters';

export function SearchSummary({
    tree,
    onTreeChange,
    criteria,
    onCriteriaChange,
    count,
    counting,
    searched,
    dirty,
    searching,
    refining,
    onRefine,
    onEditAll,
    onRun,
}: {
    tree: Group;
    onTreeChange: (t: Group) => void;
    criteria: string[];
    onCriteriaChange: (c: string[]) => void;
    count: number | null;
    counting: boolean;
    searched: boolean;
    dirty: boolean;
    searching: boolean;
    refining: boolean;
    onRefine: (instruction: string) => void;
    onEditAll: () => void;
    onRun: () => void;
}) {
    // Show the run CTA before the first search, and again whenever filters drift from the last run.
    const showRun = !searched || dirty;
    return (
        <aside className="flex w-[320px] shrink-0 flex-col border-r border-zinc-200 bg-white">
            <div className="border-b border-zinc-100 p-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-[14px] font-semibold text-zinc-900">Filters</h2>
                    <button type="button" onClick={onEditAll} className="inline-flex items-center gap-1 text-[12px] font-medium text-[#0D1830] hover:underline">
                        <SlidersHorizontal size={13} /> Edit all
                    </button>
                </div>
                <div className="mt-1 text-[12px] text-zinc-500">
                    {counting ? (
                        'counting…'
                    ) : count != null ? (
                        <>
                            <span className="font-semibold text-zinc-800">{count.toLocaleString()}</span> candidates match
                        </>
                    ) : (
                        'Adjust to see matches'
                    )}
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {/* AI refine — an NL instruction edits the current tree + criteria in place */}
                <RefineBox refining={refining} onRefine={onRefine} />

                {/* hard filters — a faithful render of the tree (TreeFilters), not a flattened projection */}
                <div className="p-4">
                    {tree.children.length > 0 ? (
                        <TreeFilters tree={tree} onChange={onTreeChange} />
                    ) : (
                        <div className="text-[12.5px] text-zinc-500">
                            No filters yet.{' '}
                            <button type="button" onClick={onEditAll} className="font-medium text-[#0D1830] hover:underline">
                                Add filters
                            </button>{' '}
                            to narrow the search.
                        </div>
                    )}
                </div>

                {/* must-have criteria — a separate config from the hard filters above */}
                <CriteriaEditor criteria={criteria} onChange={onCriteriaChange} />
            </div>

            {showRun && (
                <div className="shrink-0 border-t border-zinc-200 bg-zinc-50 p-3">
                    <button
                        type="button"
                        onClick={onRun}
                        disabled={searching}
                        className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-[#0D1830] text-[13px] font-medium text-white hover:bg-[#1C2B4D] disabled:opacity-50"
                    >
                        {searching ? 'Searching…' : searched ? 'Update search' : 'Search candidates'}
                    </button>
                    <p className="mt-1.5 text-center text-[11px] text-zinc-400">{searched ? 'Filters changed since last search' : 'Refine filters, then run the search'}</p>
                </div>
            )}
        </aside>
    );
}

/** AI refine box — a natural-language instruction that edits the current filters + criteria in place. */
function RefineBox({ refining, onRefine }: { refining: boolean; onRefine: (instruction: string) => void }) {
    const [draft, setDraft] = useState('');
    const submit = () => {
        const t = draft.trim();
        if (!t || refining) return;
        onRefine(t);
        setDraft('');
    };
    return (
        <div className="border-b border-zinc-100 p-3">
            <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#1C2B4D]">
                <Sparkles size={12} /> Update filters and criteria with AI
            </label>
            <div className="relative">
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    rows={2}
                    disabled={refining}
                    placeholder="e.g. add Python as a required skill, focus on fintech, drop the Hong Kong filter"
                    className="w-full resize-none rounded-lg border border-zinc-200 bg-white py-2 pl-2.5 pr-9 text-[12.5px] leading-snug outline-none placeholder:text-zinc-400 focus:border-[#0d1830] disabled:bg-zinc-50"
                />
                <button
                    type="button"
                    onClick={submit}
                    disabled={refining || !draft.trim()}
                    className="absolute bottom-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#0D1830] text-white hover:bg-[#1C2B4D] disabled:opacity-40"
                    aria-label="Update with AI"
                >
                    {refining ? <Loader2 size={13} className="animate-spin" /> : <ArrowUp size={14} />}
                </button>
            </div>
        </div>
    );
}

/** Soft must-have criteria editor — separate from the hard filters; add/remove/reorder inline. */
function CriteriaEditor({ criteria, onChange }: { criteria: string[]; onChange: (c: string[]) => void }) {
    const [draft, setDraft] = useState('');
    const [dragIdx, setDragIdx] = useState<number | null>(null);
    const add = () => {
        const t = draft.trim();
        if (!t) return;
        const phrase = /^the candidate/i.test(t) ? t : `The candidate ${t.charAt(0).toLowerCase()}${t.slice(1)}`;
        onChange([...criteria, phrase]);
        setDraft('');
    };
    // reorder = re-weight: criteria are scored in order of importance (earlier = weighs more).
    const move = (from: number, to: number) => {
        if (from === to || to < 0 || to >= criteria.length) return;
        const next = [...criteria];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };
    return (
        <section className="border-t-8 border-zinc-100 p-4">
            <div className="mb-1.5 flex items-center gap-1.5">
                <Sparkles size={13} className="text-[#0D1830]" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#1C2B4D]">Must-have criteria</span>
                <div className="flex-1" />
                {criteria.length > 0 && (
                    <button type="button" onClick={() => onChange([])} className="text-[12px] font-medium text-[#0D1830] hover:underline">
                        Clear
                    </button>
                )}
            </div>
            <p className="mb-2 text-[11px] leading-snug text-zinc-500">
                Soft, scored signals — one column per criterion in results. They rank candidates; they never filter the pool.
                {criteria.length > 1 && ' Drag to reorder — earlier = weighs more.'}
            </p>
            <div className="flex flex-col gap-1.5">
                {criteria.map((c, i) => (
                    <div
                        key={i}
                        draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragEnd={() => setDragIdx(null)}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (dragIdx !== null && dragIdx !== i) {
                                move(dragIdx, i);
                                setDragIdx(i);
                            }
                        }}
                        className={`group flex items-start gap-1.5 rounded-lg border border-zinc-200 bg-[#0D1830]/5 px-2 py-1.5 text-[12.5px] text-zinc-700 ${
                            dragIdx === i ? 'opacity-40' : ''
                        }`}
                    >
                        <span className="mt-px cursor-grab text-zinc-300 hover:text-zinc-500 active:cursor-grabbing" aria-hidden="true">
                            <GripVertical size={13} />
                        </span>
                        <span className="mt-px w-3.5 shrink-0 text-center text-[10.5px] font-semibold tabular-nums text-[#0D1830]">{i + 1}</span>
                        <span className="flex-1 leading-snug">{c.replace(/^the candidate /i, '').replace(/\.$/, '')}</span>
                        <button
                            type="button"
                            onClick={() => onChange(criteria.filter((_, x) => x !== i))}
                            className="text-zinc-400 opacity-40 hover:text-zinc-700 group-hover:opacity-100"
                        >
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>
            <div className="mt-2 flex items-center gap-1.5">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder="Add a must-have…"
                    className="min-w-0 flex-1 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[12px] outline-none placeholder:text-zinc-400 focus:border-[#0d1830]"
                />
                <button type="button" onClick={add} className="rounded-lg border border-zinc-200 p-1.5 text-zinc-500 hover:border-[#0d1830] hover:text-[#1c2b4d]" aria-label="Add criterion">
                    <Plus size={14} />
                </button>
            </div>
        </section>
    );
}
